import Task from "../task"
import ACLProject from "../../../gd/access-control/project/project"
import canCreateTask from "../../project/access/create-task"
import {ProjectItem} from "../../project-item/project-item"
import {ProjectAccess} from "../../../gd/const/project"

// const debug = "TaskAccessControl"
const debug = false

export class TaskAccessControl {

    private task : Task

    constructor(task:Task) {
        this.task = task
    }

    private get project() {
        return gd.session.projects.get(this.task.projectId)!
    }

    get isTaskUser() {
        if (
            this.task.actionRequiredUserId === gd.user.id ||
            this.task.assignedToUserId === gd.user.id ||
            this.task.createdByUserId === gd.user.id
        ) return true

        if (this.task.users) {
            // not implemented yet
        }

        return false
    }

    get hasAccessAndNotTemplate():boolean {

        if (this.project.isTemplate) return false

        if (this.hasAccess) return true

        return false
    }

    get hasAccessAndTaskOpenAndNotTemplate():boolean {

        // libo full|standard projects access libo task access ?

        if (this.task.isClosed) return false

        if (this.project.isTemplate) return false

        if (this.hasAccess) return true

        return false
    }


    // this is a very default check
    get hasAccessAndTaskOpen():boolean {

        // libo full|standard projects access libo task access ?

        if (this.task.isClosed) return false

        if (this.hasAccess) return true

        return false
    }

    get hasAccess():boolean {
        if (this.hasStandardAccess) {
            return true
        }

        // If the user is directly involved with the task (required user, assignee, or creator),
        // grant access even if they lack standard project access.
        if (this.isTaskUser) {
            return true
        }

        return false
    }

    get hasStandardAccess():boolean {
        // Check if the user has standard access to any of the projects associated with the task.
        // If so, the user is considered to have standard access.
        return _.some(this.task.projects, (projectItem: ProjectItem) => {
            return ACLProject.hasStandardAccess(projectItem.projectId)
        })
    }

    get hasFullAccess():boolean {
        // Checks if the user has full access to at least one project associated with the task.
        // Iterates over all projects linked to the task and returns true if any project grants full access.
        return _.some(this.task.projects, (projectItem: ProjectItem) => {
            return ACLProject.hasFullAccess(projectItem.projectId)
        })
    }

    get canReadOnly(): boolean {
        // Finds the highest access level the user has to any project associated with the task.
        let highestAccess: any = undefined
        
        _.map(this.task.projects, (projectItem: ProjectItem) => {
            const project   = gd.session.projects.get(projectItem.projectId)!
            const access    = project?.access || undefined
            // Update highestAccess if this project's access is greater than the current highest.
            if (!highestAccess || highestAccess < access) {
                highestAccess = access
            }
        })

        // Returns true if the highest access is either READ_ONLY or CLIENT (matches behavior in gd.ac.check.project.canReadOnly).
        return !!highestAccess
            && [
                ProjectAccess.READ_ONLY,
                ProjectAccess.CLIENT
            ].includes(highestAccess)
    }

    get canReadAndComment(): boolean {
        // Returns true if the user has read and comment access to at least one project linked to the task.
        // Iterates through each associated project for the task and checks permissions via ACL.
        // If any project grants 'read and comment', the method returns true.
        return _.some(this.task.projects, (projectItem: ProjectItem) => {
            return gd.ac.check.project.canReadAndComment(projectItem.projectId)
        })
    }

    get canDelete():boolean {

        if (this.hasFullAccess) {
            debug && console.log("has full access")
            return true
        }

        // Experimental allow user delete task if they created it
        if (this.task.createdByUserId === gd.user.id) {
            debug && console.log("task owner")
            return true
        }

        // Old code below

        // let res = false;
        // if (projectId) {
        //
        //     let projectAccess = 0, companyId, sProject;
        //     try {
        //         sProject = project ? project : gd.session.projects.get(projectId);
        //         projectAccess = sProject.access;
        //         companyId = sProject.companyId;
        //     } catch(e) {
        //         console.error("Check rules task canDelete error:", projectId, sProject);
        //     }
        //
        //     if (!gd.session.companies.get(companyId)) return false;
        //
        //     // let organizationRole = gd.session.companies.get(companyId).role;
        //
        //     res = (projectAccess >= gd.const.project.access.FULL/* ||
        //             organizationRole >= gd.const.companyRole.MANAGER*/);
        // }


        return false
    }

    get canChangeDeadline():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeStartDate():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeEndDate():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeDuration():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeScheduleDate():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeTaskEstimate():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeTaskProgress():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangePriority():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeActionRequired():boolean {
        return this.hasAccessAndTaskOpenAndNotTemplate
        // return this.hasAccessAndTaskOpen
    }

    get canChangeAssignedTo():boolean {
        return this.hasAccessAndTaskOpenAndNotTemplate
        // return this.hasAccessAndTaskOpen
    }

    get canChangeDependency():boolean {
        return this.hasAccessAndTaskOpen
    }
    get canChangeDurations():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeStoryPoints():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeEstimate():boolean {
        return this.hasAccessAndTaskOpen
    }

    get canChangeStatus():boolean {
        return this.hasAccessAndNotTemplate
    }

    get canChangeTaskType():boolean {
        return this.hasAccess
    }

    get canChangeTags():boolean {
        return this.hasAccess
    }

    get canChangeCRM():boolean {
        return this.hasAccess
    }

    get canChangeCustomFields():boolean {
        return true // hmmmm
    }

    get canDeleteUser():boolean {
        // return this.hasFullAccess

        // Allow only if user has full access to the default project (main or the highest in linked projects)
        return this.task.defaultProject.acl.hasFullAccess
    }

    get canAddUser(): boolean {
        // Prevent adding users to closed tasks
        if (this.task.isClosed) {
            return false
        }

        // Prevent adding users if the main project is a template
        if (this.project.isTemplate) {
            return false
        }

        // Allow only if user has standard access to the default project (main or the highest in linked projects)
        return this.task.defaultProject.acl.canAddProjectUsers
    }

    get canAddProjectUser():boolean {
        return this.hasAccessAndTaskOpenAndNotTemplate
            && ACLProject.canAddUsers(this.task.projectId)
    }

    get canAddExpense() {
        return this.hasAccess && this.project.properties.expenses
    }

    get canAddSubtask():boolean {

        if (this.task.isSubtask) return false // no sub sub tasks

        if (this.task.isClosed) return false // no add to closed items

        return canCreateTask(this.project)
    }

    get canRename(): boolean {
        return this.hasAccess
    }

    get canMove(): boolean {
        // Can move only if user has at least standard access to main project
        return this.task.project!.acl.hasStandardAccess
    }

    get canAddProject(): boolean {
        return this.hasStandardAccess
    }

    get canChangeType(): boolean {
        return this.hasAccess
    }

    get canAddTimeTracking(): boolean {
        return this.hasAccess
    }

    get canAddTimeReport(): boolean {
        return this.hasAccess
    }

    get canAddReminder(): boolean {
        return this.hasAccess
    }

    get canDuplicate(): boolean {
        if (this.task.isClosed) return false // no add to closed items

        return _.some(this.task.projects, (projectItem: ProjectItem) => {
            const project = gd.session.projects.get(projectItem.projectId)
            return project && canCreateTask(project)
        })
    }

    get canAddRecurrence(): boolean {
        return this.hasAccess
    }


}
